/* Noirarchy shared interactions (window.Noir). No dependencies; loaded with defer
   after the two vendor scripts. Clicks use delegation on document, so sections the
   theme editor re-renders keep working; per-element setup reruns on section load.

   Dialogs
     Noir.dialog.open(dialogOrId, opener)   showModal() on a <dialog> (or [open] on any
                                            other element), focus inside, Tab kept inside
     Noir.dialog.close(dialogOrId)          closes and returns focus to the opener
     [data-noir-dialog-open="id"]           opens <dialog id> on click
     [data-noir-dialog-close]               closes the dialog it sits in
     [data-noir-dialog-overlay]             closes the dialog it sits in, or the dialog whose id
                                            is its value (an overlay element)
     Escape and a click on the dialog element itself (its backdrop, when the dialog has
     no padding and a child holds the content) also close it, unless the dialog has
     [data-noir-dialog-static]. <dialog data-noir-open-on-load> opens on init.
     Events on the dialog: noir:dialog:open, noir:dialog:close.

   Carousel (scroll-snap)
     Noir.carousel(root, { track, prev, next, loop, onProgress })  returns a controller
       { next(), prev(), goTo(i), state(), update(), destroy() }
     [data-noir-carousel] roots set up automatically: track [data-noir-track], arrows
     [data-noir-prev] / [data-noir-next], [data-noir-loop] to wrap at the ends.
     onProgress(state) and the noir:carousel event get { index, count, visible, progress,
     atStart, atEnd } on every scroll frame. The root also gets the --noir-progress custom
     property (0 to 1). Each move started by an arrow or the API also fires noir:carousel:move
     with { index, source } (source is 'button' for the arrows), so autoplay can stop on input.

   Accordion
     [data-noir-accordion] is one group: at most one of its rows is open. A row is
     [data-noir-acc-item] holding a <button data-noir-acc-trigger aria-expanded
     aria-controls> (inside the row's heading) and its panel [data-noir-acc-panel]:
     .noir-collapse > .noir-collapse__inner > content (noir-base.css). An open row has
     .is-open; the panel's grid row then moves from 0fr to 1fr in 300ms
     (cubic-bezier(0.2, 0.7, 0.2, 1)) and the content fades in over 240ms. Opening a
     row closes the open one in the same frame, with the same timing, and the page
     itself never scrolls while rows move (the browser's scroll anchoring is held off
     meanwhile). When a row above the tapped one reaches past the top of the screen,
     its off-screen part goes at once with the scroll put back by the same amount in
     the same frame (nothing visible changes) and only its on-screen part folds up, so
     the tapped title rises by no more than what was on screen. Closed panels stay in
     the DOM with inert. No animation under reduced motion
     (CSS). Every row starts closed and nothing here opens one by itself.
     API: Noir.accordion.toggle(row, open?), Noir.accordion.close(row) (at once),
     Noir.accordion.init(scope). Event on the group: noir:accordion { open, item }.

   Tabs
     [data-noir-tabs] holding role="tab" buttons (aria-selected, aria-controls, roving
     tabindex) and their role="tabpanel" panels (hidden when not selected). A click or
     an arrow key (Left/Right, Up/Down, Home, End) selects a tab; selecting closes any
     open answer in the panels. A URL hash naming a panel's id selects that tab on load
     and on hashchange, and brings the tabs into view. API: Noir.tabs.select(tab).

   Also: Noir.scrollTo(target), [data-noir-scroll-to="selector"], Noir.storage.get/set/remove
   (try/catch wrapped; pass true as the last argument for sessionStorage),
   Noir.reduceMotion(), Noir.focusable(container). */
(function () {
  'use strict';

  if (window.Noir && window.Noir.__ready) return;

  var root = document.documentElement;
  root.classList.add('noir-js');

  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduceMotion() {
    return !!(motionQuery && motionQuery.matches);
  }

  var uid = 0;
  function ensureId(el, prefix) {
    if (!el.id) {
      do { uid += 1; } while (document.getElementById(prefix + uid));
      el.id = prefix + uid;
    }
    return el.id;
  }

  function resolve(target) {
    if (!target) return null;
    return typeof target === 'string' ? document.getElementById(target) : target;
  }

  function emit(el, name, detail) {
    var event;
    try {
      event = new CustomEvent(name, { bubbles: true, detail: detail || {} });
    } catch (e) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, detail || {});
    }
    el.dispatchEvent(event);
  }

  /* ---------- Storage (private windows and blocked storage throw) ---------- */
  function area(session) {
    try {
      return session ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }
  var storage = {
    get: function (key, session) {
      try {
        var s = area(session);
        return s ? s.getItem(key) : null;
      } catch (e) {
        return null;
      }
    },
    set: function (key, value, session) {
      try {
        var s = area(session);
        if (!s) return false;
        s.setItem(key, String(value));
        return true;
      } catch (e) {
        return false;
      }
    },
    remove: function (key, session) {
      try {
        var s = area(session);
        if (s) s.removeItem(key);
      } catch (e) {}
    }
  };

  /* ---------- Focus ---------- */
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';
  function focusable(container) {
    if (!container) return [];
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[hidden], [inert]')) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }
  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  /* ---------- Scroll helper ---------- */
  function scrollToTarget(target) {
    var el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    var header = document.querySelector('sticky-header, .header-wrapper');
    var offset = header ? Math.min(header.getBoundingClientRect().height, 120) + 12 : 12;
    var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: top, behavior: reduceMotion() ? 'auto' : 'smooth' });
    if (!el.hasAttribute('tabindex') && !el.matches(FOCUSABLE)) el.setAttribute('tabindex', '-1');
    focusEl(el);
  }

  /* ---------- Dialogs ---------- */
  var openStack = [];

  function isOpen(d) {
    return openStack.indexOf(d) !== -1;
  }

  function syncScrollLock() {
    root.classList.toggle('noir-dialog-open', openStack.length > 0);
  }

  function finishClose(d) {
    var i = openStack.indexOf(d);
    if (i === -1) return;
    openStack.splice(i, 1);
    syncScrollLock();
    if (d.__noirUnhid) {
      d.hidden = true;
      d.__noirUnhid = false;
    }
    var opener = d.__noirOpener;
    d.__noirOpener = null;
    if (opener && document.contains(opener)) focusEl(opener);
    emit(d, 'noir:dialog:close');
  }

  function onNativeClose() {
    finishClose(this);
  }

  function onNativeCancel(e) {
    if (this.hasAttribute('data-noir-dialog-static')) e.preventDefault();
  }

  function openDialog(target, opener) {
    var d = resolve(target);
    if (!d) return null;
    if (isOpen(d)) return d;
    d.__noirOpener = opener || (document.activeElement !== document.body ? document.activeElement : null);
    var native = typeof d.showModal === 'function';
    if (native) {
      if (!d.__noirBound) {
        d.addEventListener('close', onNativeClose);
        d.addEventListener('cancel', onNativeCancel);
        d.__noirBound = true;
      }
      try {
        if (!d.open) d.showModal();
      } catch (e) {
        d.setAttribute('open', '');
      }
    } else {
      d.setAttribute('open', '');
      if (d.hidden) {
        d.hidden = false;
        d.__noirUnhid = true;
      }
      if (!d.hasAttribute('role')) d.setAttribute('role', 'dialog');
      d.setAttribute('aria-modal', 'true');
    }
    openStack.push(d);
    syncScrollLock();
    var first = d.querySelector('[autofocus]') || focusable(d)[0];
    if (!first) {
      if (!d.hasAttribute('tabindex')) d.setAttribute('tabindex', '-1');
      first = d;
    }
    focusEl(first);
    emit(d, 'noir:dialog:open');
    return d;
  }

  function closeDialog(target) {
    var d = resolve(target);
    if (!d || !isOpen(d)) return;
    if (typeof d.close === 'function' && d.open) {
      d.close();
      /* The native close event fires later; finishClose ignores the repeat. */
      finishClose(d);
    } else {
      d.removeAttribute('open');
      finishClose(d);
    }
  }

  function topDialog() {
    return openStack.length ? openStack[openStack.length - 1] : null;
  }

  document.addEventListener('keydown', function (e) {
    var d = topDialog();
    if (!d) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (d.hasAttribute('data-noir-dialog-static')) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      closeDialog(d);
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusable(d);
    if (!items.length) {
      e.preventDefault();
      focusEl(d);
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !d.contains(active))) {
      e.preventDefault();
      focusEl(last);
    } else if (!e.shiftKey && (active === last || !d.contains(active))) {
      e.preventDefault();
      focusEl(first);
    }
  });

  /* A press that starts inside the content and ends on the backdrop is not an overlay click. */
  var pressTarget = null;
  document.addEventListener('pointerdown', function (e) {
    pressTarget = e.target;
  }, true);

  /* ---------- Carousel ---------- */
  function Carousel(el, options) {
    var self = this;
    this.root = el;
    this.options = {};
    this.track = null;
    this.frame = 0;
    this.setOptions(options);
    if (!this.track) return;

    this.onScroll = function () {
      if (self.frame) return;
      self.frame = window.requestAnimationFrame(function () {
        self.frame = 0;
        self.update();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizer = new ResizeObserver(function () { self.update(); });
      this.resizer.observe(this.track);
    } else {
      this.resizer = null;
      window.addEventListener('resize', this.onScroll);
    }
    this.update();
  }

  Carousel.prototype.setOptions = function (options) {
    var o = options || {};
    var el = this.root;
    if (o.track || !this.track) this.track = o.track || el.querySelector('[data-noir-track]');
    if (o.prev || !this.prevBtn) this.prevBtn = o.prev || el.querySelector('[data-noir-prev]');
    if (o.next || !this.nextBtn) this.nextBtn = o.next || el.querySelector('[data-noir-next]');
    if (typeof o.loop === 'boolean') this.options.loop = o.loop;
    else if (this.options.loop === undefined) this.options.loop = el.hasAttribute('data-noir-loop');
    if (typeof o.onProgress === 'function') this.options.onProgress = o.onProgress;
    return this;
  };

  Carousel.prototype.slides = function () {
    return this.track ? Array.prototype.slice.call(this.track.children) : [];
  };

  Carousel.prototype.offsetOf = function (slides, i) {
    return slides[i].offsetLeft - slides[0].offsetLeft;
  };

  Carousel.prototype.state = function () {
    var track = this.track;
    var slides = this.slides();
    var count = slides.length;
    var max = track ? Math.max(track.scrollWidth - track.clientWidth, 0) : 0;
    var left = track ? Math.abs(track.scrollLeft) : 0;
    var index = 0;
    if (count) {
      var best = Infinity;
      for (var i = 0; i < count; i++) {
        var dist = Math.abs(this.offsetOf(slides, i) - left);
        if (dist < best) { best = dist; index = i; }
      }
    }
    var step = count > 1 ? this.offsetOf(slides, 1) : (track ? track.clientWidth : 0);
    var visible = step > 0 && track ? Math.max(1, Math.round(track.clientWidth / step)) : 1;
    return {
      index: index,
      count: count,
      visible: Math.min(visible, count || 1),
      progress: max > 0 ? Math.min(left / max, 1) : 1,
      atStart: left <= 2,
      atEnd: left >= max - 2
    };
  };

  Carousel.prototype.update = function () {
    if (!this.track) return null;
    var s = this.state();
    var loop = !!this.options.loop;
    [[this.prevBtn, s.atStart], [this.nextBtn, s.atEnd]].forEach(function (pair) {
      if (!pair[0]) return;
      if (!loop && pair[1]) pair[0].setAttribute('aria-disabled', 'true');
      else pair[0].removeAttribute('aria-disabled');
    });
    this.root.style.setProperty('--noir-progress', s.progress.toFixed(4));
    if (this.options.onProgress) this.options.onProgress(s);
    emit(this.root, 'noir:carousel', s);
    return s;
  };

  Carousel.prototype.goTo = function (i, source) {
    var slides = this.slides();
    if (!slides.length) return;
    var index = Math.max(0, Math.min(i, slides.length - 1));
    var max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    var left = Math.min(this.offsetOf(slides, index), max);
    this.track.scrollTo({ left: left, behavior: reduceMotion() ? 'auto' : 'smooth' });
    emit(this.root, 'noir:carousel:move', { index: index, source: source || 'api' });
  };

  Carousel.prototype.next = function (source) {
    var s = this.state();
    if (s.atEnd) {
      if (this.options.loop) this.goTo(0, source);
      return;
    }
    this.goTo(s.index + 1, source);
  };

  Carousel.prototype.prev = function (source) {
    var s = this.state();
    if (s.atStart) {
      if (this.options.loop) this.goTo(s.count - 1, source);
      return;
    }
    this.goTo(s.index - 1, source);
  };

  Carousel.prototype.destroy = function () {
    if (!this.track) return;
    this.track.removeEventListener('scroll', this.onScroll);
    if (this.resizer) this.resizer.disconnect();
    else window.removeEventListener('resize', this.onScroll);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.root.__noirCarousel = null;
  };

  function carousel(el, options) {
    el = resolve(el);
    if (!el) return null;
    if (el.__noirCarousel) {
      el.__noirCarousel.setOptions(options);
      el.__noirCarousel.update();
      return el.__noirCarousel;
    }
    var c = new Carousel(el, options);
    if (!c.track) return null;
    el.__noirCarousel = c;
    return c;
  }

  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-noir-carousel]').forEach(function (el) {
      carousel(el);
    });
  }

  /* ---------- Accordion ---------- */
  var ACC_MS = 300;

  function accItem(el) {
    var item = el && el.closest ? el.closest('[data-noir-acc-item]') : null;
    return item && item.closest('[data-noir-accordion]') ? item : null;
  }

  function accTrigger(item) {
    return item.querySelector('[data-noir-acc-trigger]');
  }

  function accPanel(item) {
    var trigger = accTrigger(item);
    var id = trigger ? trigger.getAttribute('aria-controls') : '';
    var panel = id ? document.getElementById(id) : null;
    return panel && item.contains(panel) ? panel : item.querySelector('[data-noir-acc-panel]');
  }

  function accIsOpen(item) {
    return item.classList.contains('is-open');
  }

  /* The open rows of item's group, item excluded. */
  function accOthers(item) {
    var group = item.closest('[data-noir-accordion]');
    if (!group) return [];
    return Array.prototype.filter.call(group.querySelectorAll('[data-noir-acc-item].is-open'), function (o) {
      return o !== item && o.closest('[data-noir-accordion]') === group;
    });
  }

  /* One row's state: class, aria-expanded and inert. The CSS transition runs from
     whatever height the panel has now, so a fast second tap reverses smoothly. */
  function accApply(item, open) {
    var trigger = accTrigger(item);
    var panel = accPanel(item);
    if (open) item.classList.add('is-open');
    else item.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) {
      if (open) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    }
    var group = item.closest('[data-noir-accordion]');
    if (group) emit(group, 'noir:accordion', { open: open, item: item });
  }

  /* Rows above the tapped one that are open or still closing. */
  function accAbove(item) {
    var group = item.closest('[data-noir-accordion]');
    if (!group) return [];
    return Array.prototype.filter.call(group.querySelectorAll('[data-noir-acc-item]'), function (o) {
      if (o === item || o.closest('[data-noir-accordion]') !== group) return false;
      if (!(o.compareDocumentPosition(item) & Node.DOCUMENT_POSITION_FOLLOWING)) return false;
      var panel = accPanel(o);
      return accIsOpen(o) || (panel && panel.getBoundingClientRect().height > 0.5);
    });
  }

  /* Ends a fold-up (below) early, for a row that opens again or finished moving. */
  function accUnroll(item) {
    var panel = accPanel(item);
    if (item.__noirRoll) {
      window.clearTimeout(item.__noirRoll);
      item.__noirRoll = 0;
    }
    item.classList.remove('is-rolling');
    if (panel) {
      panel.style.gridTemplateRows = '';
      panel.style.transition = '';
    }
  }

  /* Closes a row above the tapped one whose top part is off the top of the screen.
     That part goes at once (the scroll is put back by the same amount in the same
     frame, so nothing visible changes); the part on screen then folds up with the
     usual 300ms move, its content held to the bottom edge. A row wholly off screen
     simply goes at once. Returns how much went at once. */
  function accFoldAbove(item) {
    var panel = accPanel(item);
    if (!panel) return 0;
    var r = panel.getBoundingClientRect();
    var hidden = Math.min(Math.max(-r.top, 0), r.height);
    if (hidden < 0.5) return 0;
    var visible = Math.max(r.height - hidden, 0);
    accUnroll(item);
    item.classList.add('is-rolling');
    panel.style.transition = 'none';
    panel.style.gridTemplateRows = visible + 'px';
    accApply(item, false);
    panel.getBoundingClientRect();
    panel.style.transition = '';
    panel.style.gridTemplateRows = '0px';
    item.__noirRoll = window.setTimeout(function () {
      item.__noirRoll = 0;
      if (!accIsOpen(item)) accUnroll(item);
    }, visible > 0.5 ? ACC_MS + 60 : 0);
    return hidden;
  }

  function scrollByNow(d) {
    try {
      window.scrollBy({ top: d, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollBy(0, d);
    }
  }

  /* While rows move, the browser's own scroll anchoring stays out of it (it could
     scroll the page to keep some element below the rows still), and a scroll the
     theme makes is instant (the page scrolls smoothly by default). */
  var accTimer = 0;
  function accQuiet() {
    root.classList.add('noir-acc-holding');
    window.clearTimeout(accTimer);
    accTimer = window.setTimeout(function () {
      root.classList.remove('noir-acc-holding');
    }, ACC_MS + 100);
  }

  /* Opening a row closes the other open row of its group, and every row moves with
     the same 300ms timing in the same frame; the page does not scroll while they move.
     A row above whose top is off the top of the screen loses that part at once, with
     the scroll put back in the same frame, so nothing visible jumps and the tapped
     title only rises by what was on screen. */
  function accToggle(item, open) {
    item = accItem(item);
    if (!item) return;
    var next = typeof open === 'boolean' ? open : !accIsOpen(item);
    if (next === accIsOpen(item)) return;
    var trigger = accTrigger(item);
    accQuiet();
    if (next) {
      accUnroll(item);
      if (trigger) {
        var top = trigger.getBoundingClientRect().top;
        var gone = accAbove(item).reduce(function (sum, o) {
          return sum + accFoldAbove(o);
        }, 0);
        if (gone >= 0.5) {
          var d = trigger.getBoundingClientRect().top - top;
          if (Math.abs(d) >= 0.5) scrollByNow(d);
        }
      }
      accOthers(item).forEach(function (o) {
        accApply(o, false);
      });
    }
    accApply(item, next);
  }

  /* Closes a row at once (a row hidden for another variant). */
  function accClose(item) {
    item = accItem(item);
    if (item && accIsOpen(item)) accApply(item, false);
  }

  /* Every row starts closed: its panel inert, its button not expanded. */
  function initAccordions(scope) {
    (scope || document).querySelectorAll('[data-noir-accordion] [data-noir-acc-item]').forEach(function (item) {
      if (item.__noirAcc) return;
      item.__noirAcc = true;
      var trigger = accTrigger(item);
      var panel = accPanel(item);
      if (!trigger || !panel) return;
      ensureId(panel, 'NoirAccPanel-');
      trigger.setAttribute('aria-controls', panel.id);
      var open = accIsOpen(item);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) panel.setAttribute('inert', '');
    });
  }

  /* ---------- Tabs ---------- */
  function tabList(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[role="tab"]')).filter(function (t) {
      return t.closest('[data-noir-tabs]') === root;
    });
  }

  /* Selects tab (and its panel); every other panel hides, its open answers closed. */
  function selectTab(tab, focus) {
    var root = tab && tab.closest ? tab.closest('[data-noir-tabs]') : null;
    if (!root) return;
    tabList(root).forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (!panel) return;
      panel.querySelectorAll('[data-noir-acc-item].is-open').forEach(accClose);
      panel.hidden = !on;
    });
    if (focus) {
      try {
        tab.focus({ preventScroll: true });
      } catch (e) {
        tab.focus();
      }
    }
    emit(root, 'noir:tabs', { tab: tab });
  }

  /* /page#{panel id}: open with that tab selected, and bring the tabs into view. */
  function tabFromHash(scroll) {
    var id = (window.location.hash || '').replace(/^#/, '');
    if (!id) return;
    try {
      id = decodeURIComponent(id);
    } catch (e) {}
    var panel = document.getElementById(id);
    if (!panel || panel.getAttribute('role') !== 'tabpanel') return;
    var root = panel.closest('[data-noir-tabs]');
    var tab = root ? tabList(root).filter(function (t) { return t.getAttribute('aria-controls') === id; })[0] : null;
    if (!tab) return;
    selectTab(tab, false);
    if (scroll) {
      window.requestAnimationFrame(function () {
        var header = document.querySelector('sticky-header, .header-wrapper');
        var offset = header && header.getBoundingClientRect().bottom > 0 ? Math.min(header.getBoundingClientRect().height, 120) + 12 : 12;
        window.scrollTo({ top: root.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'auto' });
      });
    }
  }

  document.addEventListener('keydown', function (e) {
    var tab = e.target && e.target.closest ? e.target.closest('[data-noir-tabs] [role="tab"]') : null;
    if (!tab) return;
    var tabs = tabList(tab.closest('[data-noir-tabs]'));
    var i = tabs.indexOf(tab);
    var next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    selectTab(tabs[next], true);
  });

  window.addEventListener('hashchange', function () {
    tabFromHash(true);
  });
  /* Images above may have moved the tabs while the page loaded. */
  window.addEventListener('load', function () {
    tabFromHash(true);
  }, { once: true });

  /* ---------- Click delegation ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var opener = t.closest('[data-noir-dialog-open]');
    if (opener) {
      e.preventDefault();
      openDialog(opener.getAttribute('data-noir-dialog-open'), opener);
      return;
    }

    var closer = t.closest('[data-noir-dialog-close]');
    if (closer) {
      var host = closer.closest('dialog, [data-noir-dialog]');
      if (host) {
        e.preventDefault();
        closeDialog(host);
        return;
      }
    }

    var top = topDialog();
    if (top && !top.hasAttribute('data-noir-dialog-static')) {
      var overlay = t.closest('[data-noir-dialog-overlay]');
      var overlayFor = overlay ? resolve(overlay.getAttribute('data-noir-dialog-overlay')) : null;
      var onBackdrop = t === top && (pressTarget === null || pressTarget === top);
      if ((overlay && (overlayFor === top || top.contains(overlay))) || onBackdrop) {
        e.preventDefault();
        closeDialog(top);
        return;
      }
    }

    var tabEl = t.closest('[data-noir-tabs] [role="tab"]');
    if (tabEl) {
      e.preventDefault();
      selectTab(tabEl, false);
      return;
    }

    var accButton = t.closest('[data-noir-acc-trigger]');
    var accRowEl = accButton ? accItem(accButton) : null;
    if (accRowEl && accTrigger(accRowEl) === accButton) {
      e.preventDefault();
      accToggle(accRowEl);
      return;
    }

    var scroller = t.closest('[data-noir-scroll-to]');
    if (scroller) {
      e.preventDefault();
      scrollToTarget(scroller.getAttribute('data-noir-scroll-to'));
      return;
    }

    var arrow = t.closest('[data-noir-prev], [data-noir-next]');
    if (arrow) {
      var host2 = arrow.closest('[data-noir-carousel]');
      var c = host2 && (host2.__noirCarousel || carousel(host2));
      if (!c || arrow.getAttribute('aria-disabled') === 'true') return;
      if (arrow.hasAttribute('data-noir-prev')) c.prev('button');
      else c.next('button');
    }
  });

  /* ---------- Init and theme editor ---------- */
  function init(scope) {
    initCarousels(scope);
    initAccordions(scope);
    if (!scope) tabFromHash(true);
    (scope || document).querySelectorAll('dialog[data-noir-open-on-load]').forEach(function (d) {
      openDialog(d);
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    openStack.slice().forEach(function (d) {
      if (e.target.contains(d)) closeDialog(d);
    });
    e.target.querySelectorAll('[data-noir-carousel]').forEach(function (el) {
      if (el.__noirCarousel) el.__noirCarousel.destroy();
    });
  });

  window.Noir = window.Noir || {};
  var api = window.Noir;
  api.__ready = true;
  api.reduceMotion = reduceMotion;
  api.storage = storage;
  api.focusable = focusable;
  api.scrollTo = scrollToTarget;
  api.dialog = { open: openDialog, close: closeDialog, top: topDialog };
  api.openDialog = openDialog;
  api.closeDialog = closeDialog;
  api.carousel = carousel;
  api.initCarousels = initCarousels;
  api.accordion = { init: initAccordions, toggle: accToggle, close: accClose };
  api.tabs = { select: selectTab };
  api.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
